import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export type ShipmentRouteStop = {
  key: string;
  kind: 'loading' | 'unloading';
  label: string;
  address?: string | null;
  city?: string | null;
  district?: string | null;
  stopOrder?: number;
  lat: number;
  lon: number;
};

export type ShipmentRouteStep = {
  instruction: string;
  name?: string | null;
  distance_m?: number;
  duration_s?: number;
  lat: number;
  lon: number;
  leg?: number;
};

export type ShipmentRouteData = {
  shipmentId: number;
  shipment_no?: string | null;
  profile?: string | null;
  source?: string | null;
  stops?: ShipmentRouteStop[];
  truckParams?: Record<string, string | number | boolean>;
  distance_km?: number | null;
  duration_h?: number | null;
  pointCount?: number | null;
  simplifiedPointCount?: number | null;
  steps?: ShipmentRouteStep[];
  stepCount?: number | null;
  computedAt?: string | null;
  geometry?: { type: 'LineString'; coordinates: [number, number][] } | null;
  simplifiedGeometry?: { type: 'LineString'; coordinates: [number, number][] } | null;
  google_maps_url?: string | null;
};

export const OSM_ROUTE_MAX_WAYPOINTS = 40;

const sampleEvenly = <T,>(points: T[], max: number): T[] => {
  if (points.length <= max) return points;
  const out: T[] = [];
  const step = (points.length - 1) / (max - 1);
  for (let i = 0; i < max; i += 1) {
    const value = points[Math.round(i * step)];
    if (out[out.length - 1] !== value) out.push(value);
  }
  return out;
};

// ORS adım adım geçiş noktalarını OpenStreetMap'in directions ekranında açılabilir
// bir permalink'e çevirir. OSM kendi motoruyla (osrm car) yeniden rota çizer,
// bu yüzden tam TIR rotası yerine geçiş noktalarının görsel doğrulamasıdır.
export const buildOsmDirectionsUrl = (route: ShipmentRouteData | null): string | null => {
  if (!route) return null;
  const steps = Array.isArray(route.steps)
    ? route.steps.filter((step) => Number.isFinite(step.lat) && Number.isFinite(step.lon))
    : [];
  const fallbackStops = Array.isArray(route.stops)
    ? route.stops.filter((stop) => Number.isFinite(stop.lat) && Number.isFinite(stop.lon))
    : [];
  const pool: { lat: number; lon: number }[] = steps.length >= 2
    ? steps
    : fallbackStops;
  if (pool.length < 2) return null;
  const picked = sampleEvenly(pool, OSM_ROUTE_MAX_WAYPOINTS);
  const routeParam = picked.map((point) => `${point.lat.toFixed(6)},${point.lon.toFixed(6)}`).join(';');
  return `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${routeParam}`;
};

export const formatStepDistance = (meters: number | null | undefined): string | null => {
  const value = Number(meters);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 1000 ? `${(value / 1000).toFixed(1)} km` : `${Math.round(value)} m`;
};

const LOADING_COLOR = '#2563eb';
const UNLOADING_COLOR = '#16a34a';
const ROUTE_COLOR = '#0f172a';

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export function ShipmentRouteMap({
  route,
  currentPosition,
  className = 'h-[420px] w-full',
}: {
  route: ShipmentRouteData | null;
  currentPosition?: { lat: number; lon: number; label?: string } | null;
  className?: string;
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.Layer[]>([]);
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { center: [39.0, 35.0], zoom: 6, zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // ORS geometrisi [lon, lat] sırasındadır; Leaflet [lat, lon] bekler.
  const latLngLine = useMemo<[number, number][]>(() => {
    const source = route?.simplifiedGeometry?.coordinates || route?.geometry?.coordinates || [];
    return source
      .filter((pair) => Array.isArray(pair) && pair.length >= 2)
      .map(([lon, lat]) => [Number(lat), Number(lon)] as [number, number])
      .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));
  }, [route]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    layersRef.current.forEach((layer) => layer.remove());
    layersRef.current = [];

    const stops = route?.stops || [];
    if (latLngLine.length > 1) {
      // Ana hat kalın, altında ince açık şerit: yoğun TIR güzergâhlarında okunurluk.
      const halo = L.polyline(latLngLine, { color: '#ffffff', weight: 9, opacity: 0.75 }).addTo(map);
      const line = L.polyline(latLngLine, { color: ROUTE_COLOR, weight: 4, opacity: 0.9 }).addTo(map);
      layersRef.current.push(halo, line);
    }

    stops.forEach((stop, index) => {
      const isLoading = stop.kind === 'loading';
      const color = isLoading ? LOADING_COLOR : UNLOADING_COLOR;
      const label = escapeHtml(stop.label || stop.key);

      const marker = L.circleMarker([stop.lat, stop.lon], {
        radius: 8,
        fillColor: color,
        fillOpacity: 0.95,
        color: '#ffffff',
        weight: 2.5,
      }).addTo(map);
      marker.bindTooltip(
        `<div style="font-family:system-ui,-apple-system,sans-serif;font-size:12px">
           <div style="font-weight:700">${index + 1}. ${label}</div>
           <div style="color:#64748b;margin-top:2px">${isLoading ? 'Yükleme' : 'Boşaltma'}</div>
         </div>`,
        { direction: 'top', offset: [0, -8] },
      );
      layersRef.current.push(marker);

      const indexIcon = L.divIcon({
        className: '',
        html: `<div style="display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:999px;background:${color};color:#fff;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);font-family:system-ui,-apple-system,sans-serif">${index + 1}</div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      layersRef.current.push(L.marker([stop.lat, stop.lon], { icon: indexIcon, interactive: false }).addTo(map));
    });

    if (showSteps) {
      const steps = (route?.steps || [])
        .filter((step) => Number.isFinite(step.lat) && Number.isFinite(step.lon));
      const cap = Math.max(1, Math.ceil(steps.length / 300));
      steps
        .filter((_, index) => index % cap === 0)
        .forEach((step, index) => {
          const marker = L.circleMarker([step.lat, step.lon], {
            radius: 4,
            fillColor: '#0ea5e9',
            fillOpacity: 0.95,
            color: '#ffffff',
            weight: 1.5,
          }).addTo(map);
          marker.bindTooltip(
            `<div style="font-family:system-ui,-apple-system,sans-serif;font-size:12px;max-width:220px">
               <div>${escapeHtml(step.instruction || '')}</div>
               ${step.name ? `<div style="color:#64748b;margin-top:2px;font-weight:600">${escapeHtml(step.name)}</div>` : ''}
             </div>`,
            { direction: 'top', offset: [0, -6] },
          );
          layersRef.current.push(marker);
          void index;
        });
    }

    if (currentPosition) {
      const vehicleIcon = L.divIcon({
        className: '',
        html: '<div style="width:0;height:0;border-left:10px solid transparent;border-right:10px solid transparent;border-bottom:18px solid #dc2626;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));transform:translate(-50%,-50%)"></div>',
        iconSize: [20, 18],
        iconAnchor: [10, 9],
      });
      const vehicle = L.marker([currentPosition.lat, currentPosition.lon], { icon: vehicleIcon, zIndexOffset: 500 }).addTo(map);
      vehicle.bindTooltip(`<div style="font-family:system-ui,-apple-system,sans-serif;font-size:12px;font-weight:700">${escapeHtml(
        currentPosition.label || 'Araç konumu',
      )}</div>`, { direction: 'top', offset: [0, -10] });
      layersRef.current.push(vehicle);
    }

    const boundsPoints = latLngLine.slice();
    stops.forEach((stop) => boundsPoints.push([stop.lat, stop.lon] as [number, number]));
    if (currentPosition) boundsPoints.push([currentPosition.lat, currentPosition.lon]);
    if (boundsPoints.length > 1) {
      map.fitBounds(L.latLngBounds(boundsPoints).pad(0.12));
    } else if (boundsPoints.length === 1) {
      map.setView(boundsPoints[0], 11);
    }
  }, [latLngLine, route, currentPosition, showSteps]);

  const stepCount = Array.isArray(route?.steps) ? route.steps.length : 0;

  return (
    <div className="relative">
      <div ref={mapRef} className={className} />
      {stepCount > 0 && (
        <button
          type="button"
          onClick={() => setShowSteps((value) => !value)}
          className={`absolute right-2 top-2 z-[1000] rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold shadow-sm transition-colors ${
            showSteps
              ? 'border-sky-300 bg-sky-600 text-white hover:bg-sky-700'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
          }`}
        >
          {showSteps ? 'Geçiş noktalarını gizle' : `${stepCount} geçiş noktası`}
        </button>
      )}
    </div>
  );
}

export function ShipmentRouteSummary({ route }: { route: ShipmentRouteData | null }) {
  if (!route) return null;
  const params = route.truckParams || {};
  const paramLabel = (key: string, unit: string) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return null;
    return `${value}${unit}`;
  };

  const chips = [
    paramLabel('weight', ' t'),
    paramLabel('height', ' m'),
    paramLabel('width', ' m'),
    paramLabel('length', ' m'),
    paramLabel('axleload', ' kg'),
    params.hazmat === true ? 'Tehlikeli madde' : null,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Mesafe</div>
          <div className="text-base font-bold text-slate-800 dark:text-slate-100">
            {route.distance_km != null ? `${route.distance_km} km` : '-'}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Tahmini Süre</div>
          <div className="text-base font-bold text-slate-800 dark:text-slate-100">
            {route.duration_h != null ? `${route.duration_h} saat` : '-'}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Durak</div>
          <div className="text-base font-bold text-slate-800 dark:text-slate-100">{route.stops?.length ?? 0}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
          <div className="text-[11px] text-slate-400">Profil</div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">TIR (HGV)</div>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              key={chip}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
            >
              {chip}
            </span>
          ))}
        </div>
      )}

      {!!route.stops?.length && (
        <ol className="space-y-1.5">
          {route.stops.map((stop, index) => (
            <li key={stop.key} className="flex items-start gap-2 text-[12px]">
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                  stop.kind === 'loading' ? 'bg-blue-600' : 'bg-emerald-600'
                }`}
              >
                {index + 1}
              </span>
              <span className="text-slate-700 dark:text-slate-300">
                <span className="font-semibold">{stop.label}</span>
                {stop.district ? ` / ${stop.district}` : ''}
                <span className="ml-1.5 text-[11px] text-slate-400">
                  {stop.kind === 'loading' ? 'Yükleme' : 'Boşaltma'}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}

      {!!route.steps?.length && (
        <div className="rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Rota Tarifi ({route.steps.length} adım)
          </div>
          <ol className="max-h-64 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
            {route.steps.map((step, index) => {
              const distance = formatStepDistance(step.distance_m);
              const legLabel =
                Array.isArray(route.stops) && step.leg != null && step.leg + 1 < route.stops.length
                  ? `${route.stops[step.leg].label} → ${route.stops[step.leg + 1].label}`
                  : null;
              return (
                <li key={index} className="flex items-start gap-2 px-3 py-1.5 text-[12px]">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-100 text-[10px] font-bold text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-700 dark:text-slate-200">{step.instruction}</div>
                    {step.name ? (
                      <div className="truncate text-[11px] font-semibold text-slate-400">{step.name}</div>
                    ) : null}
                    {legLabel ? (
                      <div className="text-[11px] text-slate-400">{legLabel}</div>
                    ) : null}
                  </div>
                  {distance ? (
                    <span className="shrink-0 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                      {distance}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
